import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Card, Table, Button, Alert, Typography, Row, Col, Input, Select, Spin, Tag } from 'antd';
import { ReloadOutlined, ClearOutlined, SearchOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { avaliacoesValidacaoService } from '../../services/api';
import { buildApiErrorMessage } from '../../utils/apiError';
import { usePermissaoRecurso } from '../../hooks/usePermissoes';
import DetalheValidacaoModal from './DetalheValidacaoModal';

const { Title, Text } = Typography;

const FILTROS_INICIAIS = { aluno: '', periodo: null, turma: null, professor: null, modalidade: null };

const formatarDataHora = (valor) => (valor ? dayjs(valor).format('DD/MM/YYYY HH:mm') : '—');

// Página "Avaliações -> Validações" (Fase 12) -- fila administrativa de
// avaliações AGUARDANDO_VALIDACAO. Recurso RBAC PRÓPRIO
// "avaliacoes_validacao", DELIBERADAMENTE distinto de "avaliacoes_gestao"
// -- visibilidade decidida por usePermissaoRecurso('avaliacoes_validacao'),
// nunca por role hardcoded (permite RH ganhar acesso só à validação, sem
// abrir critérios/períodos/turmas).
//
// Contrato real: GET /api/avaliacoes/validacao (ItemFilaValidacaoResponse)
// -- volume pequeno (dezenas), array completo, filtros/ordenação 100%
// client-side (mesma decisão já usada em Turmas/Acompanhamento).
const ValidacoesAdminPainel = () => {
  const { loading: permissaoLoading, isAdmin, podeVisualizar } = usePermissaoRecurso('avaliacoes_validacao');

  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [filtros, setFiltros] = useState(FILTROS_INICIAIS);
  const [avaliacaoSelecionada, setAvaliacaoSelecionada] = useState(null); // id_avaliacao_aluno | null

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const response = await avaliacoesValidacaoService.listarFila();
      setItens(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      const { message: mensagem } = buildApiErrorMessage(err, 'Não foi possível carregar a fila de validação.');
      setErro(mensagem);
      setItens([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const handleFiltroChange = (campo, valor) => {
    setFiltros((atual) => ({ ...atual, [campo]: valor }));
  };

  const filtroAtivo =
    filtros.aluno.trim() !== '' ||
    filtros.periodo != null ||
    filtros.turma != null ||
    filtros.professor != null ||
    filtros.modalidade != null;

  const limparFiltros = () => setFiltros(FILTROS_INICIAIS);

  // Opções dos Selects: derivadas do próprio payload já carregado (sem
  // duplicatas), nunca uma nova chamada ao backend.
  const opcoesPeriodo = useMemo(
    () => Array.from(new Set(itens.map((i) => i.nome_periodo).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [itens],
  );
  const opcoesTurma = useMemo(
    () => Array.from(new Set(itens.map((i) => i.nome_turma).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [itens],
  );
  const opcoesProfessor = useMemo(
    () => Array.from(new Set(itens.map((i) => i.nome_equipe).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [itens],
  );
  const opcoesModalidade = useMemo(
    () => Array.from(new Set(itens.map((i) => i.nome_modalidade).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [itens],
  );

  // Filtragem: sempre produz um array NOVO (nunca muta `itens`).
  const itensFiltrados = useMemo(() => {
    const termoAluno = filtros.aluno.trim().toLowerCase();
    return itens.filter((item) => {
      if (termoAluno && !String(item.nome_aluno || '').toLowerCase().includes(termoAluno)) return false;
      if (filtros.periodo && item.nome_periodo !== filtros.periodo) return false;
      if (filtros.turma && item.nome_turma !== filtros.turma) return false;
      if (filtros.professor && item.nome_equipe !== filtros.professor) return false;
      if (filtros.modalidade && item.nome_modalidade !== filtros.modalidade) return false;
      return true;
    });
  }, [itens, filtros]);

  const handleAcaoConcluida = (avaliacaoAlunoId) => {
    setItens((atual) => atual.filter((item) => item.id_avaliacao_aluno !== avaliacaoAlunoId));
  };

  const columns = [
    {
      title: 'Aluno',
      dataIndex: 'nome_aluno',
      key: 'nome_aluno',
      sorter: (a, b) => String(a?.nome_aluno || '').localeCompare(String(b?.nome_aluno || '')),
    },
    {
      title: 'Período',
      dataIndex: 'nome_periodo',
      key: 'nome_periodo',
      sorter: (a, b) => String(a?.nome_periodo || '').localeCompare(String(b?.nome_periodo || '')),
    },
    {
      title: 'Turma',
      dataIndex: 'nome_turma',
      key: 'nome_turma',
      sorter: (a, b) => String(a?.nome_turma || '').localeCompare(String(b?.nome_turma || '')),
    },
    {
      title: 'Modalidade',
      dataIndex: 'nome_modalidade',
      key: 'nome_modalidade',
      sorter: (a, b) => String(a?.nome_modalidade || '').localeCompare(String(b?.nome_modalidade || '')),
    },
    {
      title: 'Professor',
      dataIndex: 'nome_equipe',
      key: 'nome_equipe',
      sorter: (a, b) => String(a?.nome_equipe || '').localeCompare(String(b?.nome_equipe || '')),
    },
    {
      title: 'Enviado em',
      dataIndex: 'submetido_em',
      key: 'submetido_em',
      render: formatarDataHora,
      sorter: (a, b) => new Date(a?.submetido_em || 0) - new Date(b?.submetido_em || 0),
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Ações',
      key: 'acoes',
      width: 100,
      render: (_, registro) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={() => setAvaliacaoSelecionada(registro.id_avaliacao_aluno)}
        >
          Ver
        </Button>
      ),
    },
  ];

  if (permissaoLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" tip="Validando permissões..." />
      </div>
    );
  }

  if (!isAdmin && !podeVisualizar) {
    return <Navigate to="/acesso-negado" replace />;
  }

  return (
    <Card>
      <Title level={3} style={{ marginBottom: 4 }}>
        Validações
      </Title>
      <Text type="secondary">Avaliações aguardando validação antes de serem publicadas para as famílias.</Text>

      <div style={{ marginTop: 24 }}>
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Buscar aluno"
              prefix={<SearchOutlined />}
              value={filtros.aluno}
              onChange={(e) => handleFiltroChange('aluno', e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select
              placeholder="Todos os períodos"
              value={filtros.periodo}
              onChange={(valor) => handleFiltroChange('periodo', valor ?? null)}
              allowClear
              showSearch
              style={{ width: '100%' }}
              options={opcoesPeriodo.map((nome) => ({ value: nome, label: nome }))}
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select
              placeholder="Todas as turmas"
              value={filtros.turma}
              onChange={(valor) => handleFiltroChange('turma', valor ?? null)}
              allowClear
              showSearch
              style={{ width: '100%' }}
              options={opcoesTurma.map((nome) => ({ value: nome, label: nome }))}
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select
              placeholder="Todos os professores"
              value={filtros.professor}
              onChange={(valor) => handleFiltroChange('professor', valor ?? null)}
              allowClear
              showSearch
              style={{ width: '100%' }}
              options={opcoesProfessor.map((nome) => ({ value: nome, label: nome }))}
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select
              placeholder="Todas as modalidades"
              value={filtros.modalidade}
              onChange={(valor) => handleFiltroChange('modalidade', valor ?? null)}
              allowClear
              showSearch
              style={{ width: '100%' }}
              options={opcoesModalidade.map((nome) => ({ value: nome, label: nome }))}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Button icon={<ClearOutlined />} onClick={limparFiltros} disabled={!filtroAtivo} block>
              Limpar
            </Button>
          </Col>
        </Row>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <Text type="secondary">
            <Tag color="blue">{itensFiltrados.length}</Tag> aguardando validação
          </Text>
          <Button icon={<ReloadOutlined />} onClick={carregar} loading={loading}>
            Atualizar
          </Button>
        </div>

        {erro && (
          <Alert
            type="error"
            showIcon
            message="Não foi possível carregar"
            description={erro}
            style={{ marginBottom: 16 }}
            action={
              <Button size="small" icon={<ReloadOutlined />} onClick={carregar}>
                Tentar novamente
              </Button>
            }
          />
        )}

        <Table
          rowKey="id_avaliacao_aluno"
          columns={columns}
          dataSource={erro ? [] : itensFiltrados}
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          locale={{
            emptyText: erro
              ? ' '
              : itens.length > 0
                ? 'Nenhuma avaliação corresponde aos filtros.'
                : 'Nenhuma avaliação aguardando validação.',
          }}
          scroll={{ x: 900 }}
          onRow={(registro) => ({
            onClick: () => setAvaliacaoSelecionada(registro.id_avaliacao_aluno),
            style: { cursor: 'pointer' },
          })}
        />
      </div>

      <DetalheValidacaoModal
        open={avaliacaoSelecionada != null}
        avaliacaoAlunoId={avaliacaoSelecionada}
        onClose={() => setAvaliacaoSelecionada(null)}
        onAcaoConcluida={handleAcaoConcluida}
      />
    </Card>
  );
};

export default ValidacoesAdminPainel;
